import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Fab,
  Paper,
  Divider,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  ListItemButton,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Publish,
  Unpublished,
  Schedule,
  Web,
  Article,
  ShoppingCart,
  PhotoLibrary,
  Settings,
  ExpandMore,
  Save,
  Cancel,
  Upload,
  Download,
  Refresh,
  Search,
  FilterList,
  Sort,
  MoreVert,
  ContentCopy,
  Preview,
  History,
  Analytics,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Warning,
  Error,
  Info,
} from '@mui/icons-material';
import { useRbac } from '../../context/RbacContext';
import { uploadFile } from '../../api/uploadApi';

interface ContentItem {
  id: string;
  title: string;
  type: 'page' | 'blog' | 'product' | 'service';
  status: 'draft' | 'published' | 'scheduled' | 'archived';
  author: string;
  lastModified: string;
  views: number;
  slug: string;
  content: string;
  metaDescription?: string;
  tags: string[];
  featuredImage?: string;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
}

interface PageContent {
  id: string;
  page: string;
  sections: {
    hero: {
      title: string;
      subtitle: string;
      description: string;
      backgroundImage?: string;
      ctaText: string;
      ctaLink: string;
    };
    features: Array<{
      title: string;
      description: string;
      icon: string;
      color: string;
    }>;
    testimonials: Array<{
      name: string;
      company: string;
      text: string;
      rating: number;
      image?: string;
    }>;
    contact: {
      email: string;
      phone: string;
      address: string;
      socialMedia: {
        facebook?: string;
        twitter?: string;
        linkedin?: string;
        instagram?: string;
      };
    };
  };
}

const ContentManagement: React.FC = () => {
  const { hasPermission } = useRbac();
  const [activeTab, setActiveTab] = useState(0);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [pageContent, setPageContent] = useState<PageContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'content' | 'page' | 'media'>('content');
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [editingPage, setEditingPage] = useState<PageContent | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Form states
  const [contentForm, setContentForm] = useState({
    title: '',
    type: 'page' as 'page' | 'blog' | 'product' | 'service',
    content: '',
    metaDescription: '',
    tags: [] as string[],
    seoTitle: '',
    seoDescription: '',
    seoKeywords: [] as string[],
  });

  const [pageForm, setPageForm] = useState({
    page: '',
    hero: {
      title: '',
      subtitle: '',
      description: '',
      ctaText: '',
      ctaLink: '',
    },
    features: [] as Array<{
      title: string;
      description: string;
      icon: string;
      color: string;
    }>,
    testimonials: [] as Array<{
      name: string;
      company: string;
      text: string;
      rating: number;
    }>,
    contact: {
      email: '',
      phone: '',
      address: '',
      socialMedia: {
        facebook: '',
        twitter: '',
        linkedin: '',
        instagram: '',
      },
    },
  });

  const [mediaFiles, setMediaFiles] = useState<string[]>([]);
  const [mediaUploadFile, setMediaUploadFile] = useState<File | null>(null);
  const [mediaUploadPreview, setMediaUploadPreview] = useState<string | null>(null);
  const [mediaUploadLoading, setMediaUploadLoading] = useState(false);

  // Mock data
  const mockContentItems: ContentItem[] = [
    {
      id: '1',
      title: 'Home Page',
      type: 'page',
      status: 'published',
      author: 'Admin',
      lastModified: '2024-01-15T10:30:00Z',
      views: 1250,
      slug: 'home',
      content: 'Home page content...',
      metaDescription: 'Monio Manufacturing - Leading African manufacturing company',
      tags: ['home', 'manufacturing'],
      seoTitle: 'Monio Manufacturing - Premium Manufacturing Services',
      seoDescription: 'Leading African manufacturing company specializing in textiles, furniture, and metalworks',
      seoKeywords: ['manufacturing', 'Africa', 'textiles', 'furniture'],
    },
    {
      id: '2',
      title: 'The Future of African Manufacturing',
      type: 'blog',
      status: 'published',
      author: 'John Smith',
      lastModified: '2024-01-14T15:20:00Z',
      views: 450,
      slug: 'future-african-manufacturing',
      content: 'Blog post content...',
      metaDescription: 'Exploring the future trends in African manufacturing',
      tags: ['manufacturing', 'trends', 'Africa'],
      seoTitle: 'The Future of African Manufacturing: Trends to Watch',
      seoDescription: 'Discover the key trends shaping the future of manufacturing in Africa',
      seoKeywords: ['African manufacturing', 'trends', 'innovation'],
    },
    {
      id: '3',
      title: 'Premium Cotton Textiles',
      type: 'product',
      status: 'published',
      author: 'Admin',
      lastModified: '2024-01-13T09:15:00Z',
      views: 320,
      slug: 'premium-cotton-textiles',
      content: 'Product description...',
      metaDescription: 'High-quality cotton fabrics for fashion and industrial applications',
      tags: ['textiles', 'cotton', 'fabric'],
      seoTitle: 'Premium Cotton Textiles - Monio Manufacturing',
      seoDescription: 'High-quality cotton fabrics for fashion and industrial applications',
      seoKeywords: ['cotton', 'textiles', 'fabric', 'manufacturing'],
    },
  ];

  const mockPageContent: PageContent[] = [
    {
      id: '1',
      page: 'home',
      sections: {
        hero: {
          title: 'Monio Manufacturing',
          subtitle: 'Leading African Manufacturing Excellence',
          description: 'We combine traditional craftsmanship with cutting-edge technology to deliver exceptional products.',
          ctaText: 'Get Started',
          ctaLink: '/contact',
        },
        features: [
          {
            title: 'Advanced Textile Manufacturing',
            description: 'State-of-the-art textile production with sustainable practices',
            icon: 'ProductionQuantityLimits',
            color: '#6366f1',
          },
          {
            title: 'Smart Furniture Production',
            description: 'Innovative furniture with integrated technology',
            icon: 'Build',
            color: '#06b6d4',
          },
        ],
        testimonials: [
          {
            name: 'Sarah Johnson',
            company: 'Fashion Forward Ltd',
            text: 'Exceptional quality and timely delivery. Monio has exceeded our expectations.',
            rating: 5,
          },
        ],
        contact: {
          email: 'info@monio.com',
          phone: '+254 700 000 000',
          address: 'Nairobi, Kenya',
          socialMedia: {
            facebook: 'https://facebook.com/monio',
            twitter: 'https://twitter.com/monio',
            linkedin: 'https://linkedin.com/company/monio',
            instagram: 'https://instagram.com/monio',
          },
        },
      },
    },
  ];

  useEffect(() => {
    setContentItems(mockContentItems);
    setPageContent(mockPageContent);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (type: 'content' | 'page' | 'media', item?: ContentItem | PageContent) => {
    setDialogType(type);
    setOpenDialog(true);
    if (item) {
      if (type === 'content') {
        setEditingItem(item as ContentItem);
        setContentForm({
          title: (item as ContentItem).title,
          type: (item as ContentItem).type,
          content: (item as ContentItem).content,
          metaDescription: (item as ContentItem).metaDescription || '',
          tags: (item as ContentItem).tags || [],
          seoTitle: (item as ContentItem).seoTitle || '',
          seoDescription: (item as ContentItem).seoDescription || '',
          seoKeywords: (item as ContentItem).seoKeywords || [],
        });
      } else if (type === 'page') {
        setEditingPage(item as PageContent);
        const pageData = (item as PageContent).sections;
        setPageForm({
          page: (item as PageContent).page,
          hero: pageData.hero,
          features: pageData.features,
          testimonials: pageData.testimonials,
          contact: {
            email: pageData.contact.email,
            phone: pageData.contact.phone,
            address: pageData.contact.address,
            socialMedia: {
              facebook: pageData.contact.socialMedia.facebook || '',
              twitter: pageData.contact.socialMedia.twitter || '',
              linkedin: pageData.contact.socialMedia.linkedin || '',
              instagram: pageData.contact.socialMedia.instagram || '',
            },
          },
        });
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setEditingPage(null);
    setContentForm({
      title: '',
      type: 'page',
      content: '',
      metaDescription: '',
      tags: [],
      seoTitle: '',
      seoDescription: '',
      seoKeywords: [],
    });
    setPageForm({
      page: '',
      hero: {
        title: '',
        subtitle: '',
        description: '',
        ctaText: '',
        ctaLink: '',
      },
      features: [],
      testimonials: [],
      contact: {
        email: '',
        phone: '',
        address: '',
        socialMedia: {
          facebook: '',
          twitter: '',
          linkedin: '',
          instagram: '',
        },
      },
    });
  };

  const handleSaveContent = () => {
    // Save content logic
    handleCloseDialog();
  };

  const handleSavePage = () => {
    // Save page logic
    handleCloseDialog();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'scheduled':
        return 'info';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'page':
        return <Web />;
      case 'blog':
        return <Article />;
      case 'product':
        return <ShoppingCart />;
      case 'service':
        return <Settings />;
      default:
        return <Web />;
    }
  };

  const filteredContent = contentItems.filter(item => {
    const matchesSearch = (item as ContentItem).title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleMediaFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setMediaUploadFile(file);
    if (file) {
      setMediaUploadPreview(URL.createObjectURL(file));
    } else {
      setMediaUploadPreview(null);
    }
  };

  const handleMediaUpload = async () => {
    if (!mediaUploadFile) return;
    setMediaUploadLoading(true);
    try {
      const uploadResult = await uploadFile({ file: mediaUploadFile, type: 'cms' });
      setMediaFiles((prev) => [`/uploads/cms/${uploadResult.filename}`, ...prev]);
      setMediaUploadFile(null);
      setMediaUploadPreview(null);
    } catch (err) {
      // handle error
    } finally {
      setMediaUploadLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Content Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Content
              </Typography>
              <Typography variant="h4">{contentItems.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Published
              </Typography>
              <Typography variant="h4" color="success.main">
                {contentItems.filter(item => item.status === 'published').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Drafts
              </Typography>
              <Typography variant="h4" color="warning.main">
                {contentItems.filter(item => item.status === 'draft').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Views
              </Typography>
              <Typography variant="h4" color="primary.main">
                {contentItems.reduce((sum, item) => sum + item.views, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Content" icon={<Article />} />
          <Tab label="Pages" icon={<Web />} />
          <Tab label="Media" icon={<PhotoLibrary />} />
          <Tab label="SEO" icon={<TrendingUp />} />
        </Tabs>
      </Paper>

      {/* Content Tab */}
      {activeTab === 0 && (
        <Box>
          {/* Filters and Actions */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="page">Pages</MenuItem>
                <MenuItem value="blog">Blog Posts</MenuItem>
                <MenuItem value="product">Products</MenuItem>
                <MenuItem value="service">Services</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('content')}
            >
              Add Content
            </Button>
          </Box>

          {/* Content Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell>Views</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContent.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{item.title}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          /{item.slug}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getTypeIcon(item.type)}
                        label={item.type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={getStatusColor(item.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.author}</TableCell>
                    <TableCell>
                      {new Date(item.lastModified).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{item.views.toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('content', item)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Pages Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('page')}
            >
              Edit Page Content
            </Button>
          </Box>

          <Grid container spacing={3}>
            {pageContent.map((page) => (
              <Grid item xs={12} md={6} key={page.id}>
                <Card>
                  <CardHeader
                    title={page.page.charAt(0).toUpperCase() + page.page.slice(1)}
                    action={
                      <IconButton onClick={() => handleOpenDialog('page', page)}>
                        <Edit />
                      </IconButton>
                    }
                  />
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Hero Section
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {page.sections.hero.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {page.sections.hero.subtitle}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="h6" gutterBottom>
                      Features ({page.sections.features.length})
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {page.sections.features.map(f => f.title).join(', ')}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="h6" gutterBottom>
                      Contact Info
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {page.sections.contact.email}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {page.sections.contact.phone}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Media Tab */}
      {activeTab === 2 && (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Upload />}
              component="label"
              disabled={mediaUploadLoading}
            >
              Upload Media
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleMediaFileChange}
              />
            </Button>
            {mediaUploadPreview && (
              <Box sx={{ mt: 2 }}>
                <img src={mediaUploadPreview} alt="Preview" style={{ maxWidth: 200, maxHeight: 200 }} />
                <Button onClick={handleMediaUpload} variant="contained" sx={{ ml: 2 }} disabled={mediaUploadLoading}>
                  {mediaUploadLoading ? 'Uploading...' : 'Confirm Upload'}
                </Button>
              </Box>
            )}
          </Box>
          <ImageList cols={4} gap={8}>
            {mediaFiles.map((url, idx) => (
              <ImageListItem key={url}>
                <ListItemButton>
                  <img
                    src={url}
                    alt={`Media ${idx + 1}`}
                    loading="lazy"
                  />
                  <ImageListItemBar
                    title={`Image ${idx + 1}`}
                    subtitle={`Uploaded`}
                    actionIcon={
                      <IconButton size="small">
                        <MoreVert />
                      </IconButton>
                    }
                  />
                </ListItemButton>
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}

      {/* SEO Tab */}
      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            SEO Management
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Meta Tags" />
                <CardContent>
                  <TextField
                    fullWidth
                    label="Default Title"
                    defaultValue="Monio Manufacturing - Leading African Manufacturing"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Default Description"
                    defaultValue="Leading African manufacturing company specializing in textiles, furniture, and metalworks"
                    margin="normal"
                    multiline
                    rows={3}
                  />
                  <TextField
                    fullWidth
                    label="Default Keywords"
                    defaultValue="manufacturing, Africa, textiles, furniture, metalworks"
                    margin="normal"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Social Media" />
                <CardContent>
                  <TextField
                    fullWidth
                    label="Facebook App ID"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Twitter Handle"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Google Analytics ID"
                    margin="normal"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Content Dialog */}
      <Dialog open={openDialog && dialogType === 'content'} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Content' : 'Add New Content'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={contentForm.title}
                onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={contentForm.type}
                  onChange={(e) => setContentForm({ ...contentForm, type: e.target.value as any })}
                  label="Type"
                >
                  <MenuItem value="page">Page</MenuItem>
                  <MenuItem value="blog">Blog Post</MenuItem>
                  <MenuItem value="product">Product</MenuItem>
                  <MenuItem value="service">Service</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content"
                value={contentForm.content}
                onChange={(e) => setContentForm({ ...contentForm, content: e.target.value })}
                multiline
                rows={6}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Description"
                value={contentForm.metaDescription}
                onChange={(e) => setContentForm({ ...contentForm, metaDescription: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SEO Title"
                value={contentForm.seoTitle}
                onChange={(e) => setContentForm({ ...contentForm, seoTitle: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SEO Description"
                value={contentForm.seoDescription}
                onChange={(e) => setContentForm({ ...contentForm, seoDescription: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveContent} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Page Dialog */}
      <Dialog open={openDialog && dialogType === 'page'} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Edit Page Content
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Hero Section
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hero Title"
                value={pageForm.hero.title}
                onChange={(e) => setPageForm({
                  ...pageForm,
                  hero: { ...pageForm.hero, title: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hero Subtitle"
                value={pageForm.hero.subtitle}
                onChange={(e) => setPageForm({
                  ...pageForm,
                  hero: { ...pageForm.hero, subtitle: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hero Description"
                value={pageForm.hero.description}
                onChange={(e) => setPageForm({
                  ...pageForm,
                  hero: { ...pageForm.hero, description: e.target.value }
                })}
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={pageForm.contact.email}
                onChange={(e) => setPageForm({
                  ...pageForm,
                  contact: { ...pageForm.contact, email: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={pageForm.contact.phone}
                onChange={(e) => setPageForm({
                  ...pageForm,
                  contact: { ...pageForm.contact, phone: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={pageForm.contact.address}
                onChange={(e) => setPageForm({
                  ...pageForm,
                  contact: { ...pageForm.contact, address: e.target.value }
                })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSavePage} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog('content')}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default ContentManagement; 